import { Card, CardContent, CardHeader, CardTitle, IdCell } from '@resource-kit/ui/react';
import { KpiCard } from '@components/ui/KpiCard';
import type { components } from '@/types/api.generated';
import { AlertTriangle, Clock, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DispatchFunnel } from './DispatchFunnel';
import { SalesBridgeCard } from './SalesBridgeCard';

// The dispatch preview is a SUMMARY (#935): planned/dispatchable counts plus
// the stop-already-dispatched list; anomalies live in `PreDispatchChecks` (#2474).
type DispatchPreview = components['schemas']['DispatchPreview'];
// #2433: under consolidation, a delivery job whose same-address driver task was
// already dispatched — the run dispatches it as its own driver task.
type StopAlreadyDispatched = DispatchPreview['stopAlreadyDispatched'][number];

type StatusCounts = DispatchPreview['statusCounts'];

// Fixed display order for the day-job status breakdown (#1533). `satisfies`
// pins every key against the backend `statusCounts` shape so a renamed /
// dropped status fails compilation here, not silently at runtime.
const STATUS_ORDER = [
  'planned',
  'dispatched',
  'in_progress',
  'completed',
  'exception',
  'cancelled',
] as const satisfies readonly (keyof StatusCounts)[];

interface DispatchOverviewSectionProps {
  date: string;
  dispatchable: number;
  /** Whole-day job total (all statuses) — the KPI-1 headline value. */
  total: number;
  /** Per-status counts backing the KPI-1 breakdown line. */
  statusCounts?: StatusCounts;
  /** Jobs whose same-address driver task is already dispatched (#2433). */
  stopAlreadyDispatched: StopAlreadyDispatched[];
  previewLoading: boolean;
  reviewLoading: boolean;
  totalReviewItems: number;
}

/**
 * Dispatch overview — the body of the "more checks" row on the dispatch page:
 * preview KPIs, sales context, and dispatch anomalies. Extracted from
 * `DispatchDashboard` as a presentational section.
 */
export function DispatchOverviewSection({
  date,
  dispatchable,
  total,
  statusCounts,
  stopAlreadyDispatched,
  previewLoading,
  reviewLoading,
  totalReviewItems,
}: DispatchOverviewSectionProps) {
  const { t } = useTranslation();
  // KPI-1 breakdown: iterate the fixed status order, drop zero counts, and
  // render "<label> <count>" joined by " · ". Skipping zeros means a day with
  // jobs never surfaces a misleading `0` (the whole point of #1533).
  const statusBreakdown = statusCounts
    ? STATUS_ORDER.filter((s) => statusCounts[s] > 0)
        .map((s) => `${t(`job.enum.${s}`)} ${statusCounts[s]}`)
        .join(' · ')
    : '';
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('dispatch.dayJobs')}
          icon={<Clock className="h-4 w-4 text-muted-foreground" />}
          loading={previewLoading}
          value={total}
          description={total === 0 ? t('dispatch.dayJobsEmpty') : statusBreakdown}
        />

        <KpiCard
          label={t('dispatch.dispatchable')}
          icon={<Truck className="h-4 w-4 text-muted-foreground" />}
          loading={previewLoading}
          value={dispatchable}
          description={t('dispatch.overview.runExplainer')}
        />

        <KpiCard
          to="/admin/review-queue"
          label={t('dispatch.openReviewItems')}
          icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}
          loading={reviewLoading}
          value={totalReviewItems}
          description={
            totalReviewItems === 0
              ? t('dispatch.noOpenReviewItems')
              : t('dispatch.reviewItemsPending', { count: totalReviewItems })
          }
        />
      </div>

      <DispatchFunnel date={date} />

      <SalesBridgeCard date={date} />

      {stopAlreadyDispatched.length > 0 ? (
        <Card data-testid="dispatch-stop-already-dispatched">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-muted-foreground" />
              {t('dispatch.stopAlreadyDispatchedTitle', { count: stopAlreadyDispatched.length })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-2 text-left font-medium">{t('dispatch.columnOrder')}</th>
                    <th className="px-4 py-2 text-left font-medium">
                      {t('dispatch.columnReason')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stopAlreadyDispatched.map((row) => (
                    <tr key={row.jobId} className="border-b last:border-0">
                      <td className="px-4 py-2">
                        <IdCell value={row.orderId} truncate={16} />
                      </td>
                      <td className="px-4 py-2">
                        {t('dispatch.stopAlreadyDispatchedReason', {
                          orderName: row.siblingOrderName,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}
